import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { reactionsApi } from "@/react/api";
import type { ReactionAttributes } from "@/react/types/Chat/ChatReaction";
import {
  useChatMessageReaction,
  useChatThreadReaction,
} from "@circle-react/hooks/chatsV2";

export interface ReactionProps {
  communityMemberIds?: number[];
  currentCommunityMemberId: number;
  emoji: string;
  reactionableId: number;
  reactionableType?: string;
  setCommunityMembers?: (data: any) => void;
}

export const useReactions = ({
  currentCommunityMemberId,
  emoji,
  reactionableId,
  reactionableType,
  setCommunityMembers,
  communityMemberIds = [],
}: ReactionProps) => {
  const { createReactionFromMessage, removeReactionFromMessage } =
    useChatMessageReaction();

  const { createReactionFromThread, removeReactionFromThread } =
    useChatThreadReaction();

  const [isLoading, setIsLoading] = useState(false);

  const addReaction = (body: any) => {
    setIsLoading(true);
    onCreateReactionCallback({
      communityMemberId: currentCommunityMemberId,
      emoji: body.emoji,
      reactionableId: body.chat_room_message,
    });

    void addReactionRequest(body);
  };

  const removeReaction = (body: any) => {
    setIsLoading(true);
    onRemoveReactionCallback({
      communityMemberId: currentCommunityMemberId,
      emoji: body.emoji,
      reactionableId: body.chat_room_message,
    });

    void removeReactionRequest(body);
  };

  const { mutateAsync: addReactionRequest } = useMutation<any, unknown, any>(
    body => reactionsApi.addReaction(body),
    {
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  const { mutateAsync: removeReactionRequest } = useMutation(
    body => reactionsApi.removeReaction(body),
    {
      onSettled: () => {
        setIsLoading(false);
      },
    },
  );

  const { refetch: fetchCommunityMembers } = useQuery<any, unknown, void>(
    ["reaction", reactionableId, reactionableType, emoji],
    () =>
      reactionsApi.fetchCommunityMemberDetails({
        ids: communityMemberIds.join(","),
      }),
    {
      onSuccess: data => {
        if (!setCommunityMembers) return;
        setCommunityMembers(data);
      },
      enabled: false,
    },
  );

  const onCreateReactionCallback = (reactionAttributes: ReactionAttributes) => {
    createReactionFromMessage?.(reactionAttributes);
    createReactionFromThread?.(reactionAttributes);
  };

  const onRemoveReactionCallback = (reactionAttributes: ReactionAttributes) => {
    removeReactionFromMessage?.(reactionAttributes);
    removeReactionFromThread?.(reactionAttributes);
  };

  return {
    addReaction,
    removeReaction,
    fetchCommunityMembers,
    isLoading,
  };
};
